import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { pickGreeting } from '../../shared/greetings.js'

import { useOwnerSession } from '../hooks/useOwnerSession.js'
import { useBranches } from '../hooks/useBranches.js'
import { useDashboardStats } from '../hooks/useDashboardStats.js'
import { usePrograms } from '../program/hooks/useProgram.js'
import { SetupCompleteCTA } from '../components/SetupCompleteCTA.js'
import {
  StampConfirmOverlay,
  type StampCard,
} from '../components/StampConfirmOverlay.js'
import { MembershipScanController } from '../../shared/membership/MembershipScanController.js'
import '../../shared/redemption/redemptionUI.css'
import '../../staff/pages/staff.css'
import './stampPage.css'

const LAST_BRANCH_KEY = 'naya:lastUsedBranchId'

interface MembershipScanState {
  card_id: string
  raw_token: string
  branch_id: string | null
}

interface DashboardLocationState {
  stampCard?: StampCard
  // SLICE-MEMBERSHIP-PASS — a membership scan resolved on /owner/stamp.
  membershipScan?: MembershipScanState
}

/**
 * Owner dashboard — deliberately thin in 4a-1. Renders a greeting, the
 * derived setup / logo nudges, a "first loyalty program" empty-state
 * placeholder (real flow is 4b), and placeholder quick-stats. No real metric
 * fetching — the data sources matter in 4b.
 */
export function DashboardPage() {
  const { data: me } = useOwnerSession()
  const branches = useBranches()
  const stats = useDashboardStats()
  const programs = usePrograms()
  const location = useLocation()
  const navigate = useNavigate()

  // 4b iteration — when /owner/stamp navigates here with a resolved card we
  // capture it ONCE into local state, then immediately clear the router
  // history entry so a back-navigation or accidental refresh doesn't re-pop
  // the overlay. Local state owns the overlay's lifetime from this point on.
  const [stampCard, setStampCard] = useState<StampCard | null>(null)
  // SLICE-MEMBERSHIP-PASS — a membership scan from /owner/stamp opens the
  // read-only verify panel (no stamp). Same capture-once-then-clear pattern as
  // the loyalty stamp card so a back-navigation can't re-pop it.
  const [membershipScan, setMembershipScan] =
    useState<MembershipScanState | null>(null)
  useEffect(() => {
    const state = location.state as DashboardLocationState | null
    if (state?.stampCard) {
      setStampCard(state.stampCard)
      navigate(location.pathname, { replace: true, state: {} })
    } else if (state?.membershipScan) {
      setMembershipScan(state.membershipScan)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  // The overlay needs a branchId to issue stamps. /owner/stamp persists the
  // active branchId in localStorage so the overlay can pick it up here.
  const stampBranchId =
    typeof window !== 'undefined'
      ? localStorage.getItem(LAST_BRANCH_KEY)
      : null

  const tenant = me?.tenant
  const firstName = me?.user.display_name?.split(' ')[0] ?? tenant?.name ?? 'there'
  // Pick one greeting at mount and hold it for the page's lifetime — a fresh
  // pick happens on the next reload/navigation so the line feels alive but
  // doesn't flicker mid-session.
  const greeting = useMemo(() => pickGreeting(firstName), [firstName])

  // Derived "setup incomplete" signal — the wizard creates the first branch
  // on its last step, so zero branches means setup wasn't finished. While the
  // branch list is still loading we don't show the CTA (avoids a flash).
  const setupIncomplete =
    branches.isSuccess && branches.data.branches.length === 0

  const needsLogo = tenant ? tenant.logo_url == null : false

  // The stamp + enroll flows can't function until the tenant has at least one
  // ACTIVE loyalty program — there's nothing to stamp against otherwise. We
  // only show the *disabled* state once we actually KNOW none is active: while
  // the query is in flight we keep the tiles enabled so an established tenant
  // (the common case) sees a stable, instant dashboard instead of a
  // disabled→enabled flash on every visit.
  const programKnownNotActive =
    programs.isSuccess &&
    !programs.data.programs.some((p) => p.status === 'active')
  const programReady = !programKnownNotActive
  const showConfigureProgram = programKnownNotActive

  return (
    <div>
      <h1 className="owner-greeting">{greeting}</h1>
      <p className="owner-greeting-sub">
        Here's your {tenant?.name ?? 'business'} dashboard.
      </p>

      {setupIncomplete && <SetupCompleteCTA />}

      {needsLogo && (
        <div className="owner-nudge">
          <div className="owner-nudge-body">
            <div className="owner-nudge-title">Add your business logo</div>
            <p className="owner-nudge-text">
              Your loyalty passes currently use the Naya default logo. Upload
              your own so customers recognise your brand.
            </p>
          </div>
          <Link
            to="/branding"
            className="owner-btn owner-btn-primary owner-btn-sm"
          >
            Add logo
          </Link>
        </div>
      )}

      {/* Top stats — four indexed COUNT queries server-side, 60s stale on
          the client (useDashboardStats). Em-dash while loading; on error
          we still render dashes rather than blocking the page.
          Order is intentional: short-term ↔ lifetime on row 1, inventory ↔
          engagement on row 2. */}
      <div className="owner-stats">
        <div className="owner-stat">
          <div className="owner-stat-value">
            {stats.data ? stats.data.stamps_today.toLocaleString() : '—'}
          </div>
          <div className="owner-stat-label">Stamps today</div>
        </div>
        <div className="owner-stat">
          <div className="owner-stat-value">
            {stats.data ? stats.data.total_stamps.toLocaleString() : '—'}
          </div>
          <div className="owner-stat-label">Total stamps</div>
        </div>
        <div className="owner-stat">
          <div className="owner-stat-value">
            {stats.data ? stats.data.active_passes.toLocaleString() : '—'}
          </div>
          <div className="owner-stat-label">Active passes</div>
        </div>
        <div className="owner-stat">
          <div className="owner-stat-value">
            {stats.data ? stats.data.active_customers.toLocaleString() : '—'}
          </div>
          <div className="owner-stat-label">Active customers</div>
        </div>
      </div>

      {/* Primary actions, mirroring the staff home layout. The configure-
          program tile only appears until the program is published; once
          active it disappears (the program is set up — no nudge needed).
          Stamp + enroll stay disabled until then since neither can function
          without an active program. */}
      <div className="staff-home-actions">
        {showConfigureProgram && (
          <Link
            to="/owner/program"
            className="staff-home-card staff-home-stamp"
          >
            <ConfigureGlyph />
            <span className="staff-home-card-title">
              Configure your loyalty program
            </span>
            <span className="staff-home-card-sub">
              Set up your stamp card and publish it to start enrolling
            </span>
          </Link>
        )}

        {programReady ? (
          <Link to="/owner/stamp" className="staff-home-card staff-home-stamp">
            <StampGlyph />
            <span className="staff-home-card-title">Stamp a customer</span>
            <span className="staff-home-card-sub">
              Scan their loyalty pass QR
            </span>
          </Link>
        ) : (
          <div
            className="staff-home-card staff-home-card-disabled"
            aria-disabled="true"
          >
            <StampGlyph />
            <span className="staff-home-card-title">Stamp a customer</span>
            <span className="staff-home-card-sub">
              Available after you publish your loyalty program
            </span>
          </div>
        )}

        {programReady ? (
          <Link
            to="/owner/enroll"
            className="staff-home-card staff-home-enroll"
          >
            <EnrollGlyph />
            <span className="staff-home-card-title">
              Enroll a new customer
            </span>
            <span className="staff-home-card-sub">
              Collect their details, hand off a QR
            </span>
          </Link>
        ) : (
          <div
            className="staff-home-card staff-home-card-disabled"
            aria-disabled="true"
          >
            <EnrollGlyph />
            <span className="staff-home-card-title">
              Enroll a new customer
            </span>
            <span className="staff-home-card-sub">
              Available after you publish your loyalty program
            </span>
          </div>
        )}
      </div>

      {stampCard && (
        <StampConfirmOverlay
          card={stampCard}
          branchId={stampBranchId}
          onDone={() => setStampCard(null)}
        />
      )}

      {membershipScan && (
        <MembershipScanController
          cardId={membershipScan.card_id}
          rawToken={membershipScan.raw_token}
          branchId={membershipScan.branch_id ?? stampBranchId}
          endpoint="owner"
          onDone={() => setMembershipScan(null)}
        />
      )}
    </div>
  )
}

function ConfigureGlyph() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="8" cy="11" r="1.6" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="14" cy="11" r="1.6" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 20.5l1.4-1.4M12 20.5l-1.4-1.4M12 20.5V21"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function StampGlyph() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M8 12l3 3 5-5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function EnrollGlyph() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1.8" />
      <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1.8" />
      <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M14 14h4M14 18h4M14 21h2M21 14v7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
